import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import Page from "../../common/layout/Page";
import Loader from "../../common/Loader";
import {
  getAgentInterrupteurs,
  setAgentInterrupteur,
} from "../../../services/graal-api/leadAutoAgentService";

// Compagnies que l'agent sait tarifer, dans l'ordre du référentiel de graal
// (config services.agent-tarification.compagnies, PER-2498) : graal n'expose
// pas ce référentiel hors d'une demande, la liste est tenue ici. Allianz (Prima)
// est inactive côté graal tant que Perceval ne l'allume pas ; ses interrupteurs
// se règlent déjà ici, pour que le « déclenchement automatique » soit ouvert le
// jour venu (PER-2500).
const COMPAGNIES = [
  { id: "assurali", label: "AssurAli" },
  { id: "prima", label: "Allianz (Prima)" },
];

/**
 * Paramètres › Agent de tarification (PER-2434) : les interrupteurs de
 * l'agent, par compagnie. Fermé : l'agent s'arrête et un bouton apparaît dans
 * la timeline de la demande ; ouvert : il enchaîne seul. Chaque changement est
 * tracé (auteur, date) côté graal et relu par l'agent au début de son
 * prochain job.
 */
const AgentTarificationSettings = () => {
  const [compagnie, setCompagnie] = useState(COMPAGNIES[0].id);
  const [interrupteurs, setInterrupteurs] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [enCours, setEnCours] = useState(null);
  const [errors, setErrors] = useState({});

  const charger = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await getAgentInterrupteurs(compagnie);
      setInterrupteurs(data.interrupteurs);
      setErrors({});
    } catch (e) {
      setErrors({ other: "Les interrupteurs n'ont pas pu être chargés depuis graal." });
    } finally {
      setIsLoading(false);
    }
  }, [compagnie]);

  useEffect(() => {
    charger();
  }, [charger]);

  const basculer = async (interrupteur) => {
    const ouvert = !interrupteur.ouvert;
    if (
      ouvert &&
      !window.confirm(
        `Ouvrir « ${interrupteur.libelle} » ? ${interrupteur.description}\n\nL'agent enchaînera cette action sans demander de confirmation.`,
      )
    ) {
      return;
    }
    setEnCours(interrupteur.nom);
    try {
      const { data } = await setAgentInterrupteur({ compagnie, nom: interrupteur.nom, ouvert });
      setInterrupteurs((old) => old.map((i) => (i.nom === data.nom ? data : i)));
      toast.success(`« ${interrupteur.libelle} » ${ouvert ? "ouvert" : "fermé"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Le changement n'a pas pu être enregistré.");
    } finally {
      setEnCours(null);
    }
  };

  return (
    <Page title="Agent de tarification" errors={errors}>
      <p className="text-muted">
        L'agent de tarification travaille sur les demandes auto finalisées : il relit les
        réponses du formulaire, obtient les tarifs chez la compagnie, crée le projet, produit
        le devis et le fait envoyer au prospect. Chaque action irréversible est derrière un
        interrupteur. <strong>Fermé</strong>, l'agent s'arrête avant et un bouton apparaît dans
        la timeline de la demande ; <strong>ouvert</strong>, il enchaîne seul.
      </p>

      {COMPAGNIES.length > 1 && (
        <div className="form-group" style={{ maxWidth: 320 }}>
          <label htmlFor="compagnie-agent">Compagnie</label>
          <select
            id="compagnie-agent"
            className="form-control"
            value={compagnie}
            onChange={(e) => setCompagnie(e.target.value)}
          >
            {COMPAGNIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoading || !interrupteurs ? (
        <Loader withContainer={true} />
      ) : (
        <Liste>
          {interrupteurs.map((interrupteur) => (
            <Ligne key={interrupteur.nom} ouvert={interrupteur.ouvert}>
              <label className="d-flex align-items-start" style={{ gap: 12, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={interrupteur.ouvert}
                  disabled={enCours === interrupteur.nom}
                  onChange={() => basculer(interrupteur)}
                  aria-label={interrupteur.libelle}
                  style={{ marginTop: 5, width: 18, height: 18 }}
                />
                <div>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                    <strong>{interrupteur.libelle}</strong>
                    <Etat ouvert={interrupteur.ouvert}>
                      {interrupteur.ouvert ? "Ouvert : automatique" : "Fermé : bouton dans la timeline"}
                    </Etat>
                    {enCours === interrupteur.nom && <i className="fa fa-spinner fa-spin" />}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    {interrupteur.description}
                  </div>
                  {interrupteur.modifieLe && (
                    <small className="text-muted">
                      Modifié le {new Date(interrupteur.modifieLe).toLocaleString("fr-FR")}
                      {interrupteur.modifiePar ? ` par ${interrupteur.modifiePar}` : ""}
                    </small>
                  )}
                </div>
              </label>
            </Ligne>
          ))}
        </Liste>
      )}
    </Page>
  );
};

const Liste = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 820px;
`;
const Ligne = styled.div`
  padding: 14px 16px;
  border-radius: 6px;
  border: 1px solid ${({ ouvert }) => (ouvert ? "#16a34a" : "#dee2e6")};
  background: ${({ ouvert }) => (ouvert ? "#16a34a0d" : "white")};
`;
const Etat = styled.span`
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 999px;
  color: white;
  background: ${({ ouvert }) => (ouvert ? "#16a34a" : "#6c757d")};
`;

export default AgentTarificationSettings;
